import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";

export default function DynamicEQLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/dynamic-eq";
  const pageTitle = "Dynamic EQ in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what dynamic EQ is, how EQ bands move only when needed, how it differs from standard EQ and multi-band compression, and how it is used in mixing and mastering.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "dynamic EQ",
          "what is dynamic eq",
          "dynamic equalizer",
          "dynamic eq vs multiband compression",
          "de-harshing",
          "frequency dependent processing",
          "mixing dynamic eq",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Dynamic EQ" },
          { "@type": "Thing", name: "Equalization" },
          { "@type": "Thing", name: "Multi-band compression" },
          { "@type": "Thing", name: "De-essing" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Mastering" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Dynamic EQ",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Dynamic EQ changes EQ only when the selected frequency range becomes too strong or needs more emphasis.",
      icon: <EqualizerRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It solves frequency problems only when they happen, which often sounds more natural than static cuts or boosts.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "De-harshing, de-essing, vocal cleanup, low-end control, mix bus refinement, mastering, and frequency-specific problem solving.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const concepts = [
    {
      name: "Static EQ",
      description:
        "A normal EQ cut or boost stays the same all the time, whether the problem frequency is present or not.",
    },
    {
      name: "Dynamic EQ",
      description:
        "A dynamic EQ band reacts to signal level and only moves when the target frequency range crosses a threshold or trigger condition.",
    },
    {
      name: "Frequency-Specific Response",
      description:
        "This makes it possible to control a narrow or broad problem area without changing the entire sound constantly.",
    },
    {
      name: "Targeted Transparency",
      description:
        "Dynamic EQ is often valued because it can sound more precise and less heavy-handed than broader dynamic tools.",
    },
  ];

  const commonUses = [
    {
      name: "De-Essing",
      description:
        "A classic use where only the sibilant range is reduced when it becomes too strong.",
    },
    {
      name: "De-Harshing",
      description:
        "Controls upper-mid or high-frequency harshness only when the source gets aggressive.",
    },
    {
      name: "Boom Control",
      description:
        "Reduces excess low-end buildup only when it becomes too prominent.",
    },
    {
      name: "Vocal Cleanup",
      description:
        "Useful for controlling resonances, chest buildup, or presence spikes that do not happen evenly all the time.",
    },
    {
      name: "Mix Bus Refinement",
      description:
        "Makes subtle corrections in mastering or bus work without broad static tonal changes.",
    },
    {
      name: "Selective Enhancement",
      description:
        "Some dynamic EQs can also boost dynamically, emphasizing frequencies only when useful instead of boosting them all the time.",
    },
  ];

  const controls = [
    {
      name: "Frequency",
      description:
        "Selects the center frequency or target area the EQ band is focusing on.",
    },
    {
      name: "Q / Bandwidth",
      description:
        "Controls how narrow or wide the EQ band is, which determines how much of the surrounding spectrum is affected.",
    },
    {
      name: "Threshold",
      description:
        "Sets when the dynamic movement begins based on signal level in that frequency range.",
    },
    {
      name: "Range / Amount",
      description:
        "Limits how far the band can cut or boost dynamically.",
    },
    {
      name: "Attack and Release",
      description:
        "Control how quickly the dynamic EQ engages and recovers, which strongly affects transparency and feel.",
    },
    {
      name: "Sidechain / External Trigger",
      description:
        "Some dynamic EQs can react to another signal, which allows more advanced frequency-specific ducking and cleanup.",
    },
  ];

  const comparisons = [
    {
      name: "Standard EQ",
      description:
        "Best when the tonal problem is constant and needs a fixed adjustment.",
    },
    {
      name: "Dynamic EQ",
      description:
        "Best when the problem is intermittent and you only want correction when necessary.",
    },
    {
      name: "Multi-Band Compression",
      description:
        "Often broader and more band-based, while dynamic EQ can be narrower and more surgical depending on the tool.",
    },
  ];

  const bestPractices = [
    "Use dynamic EQ when the problem is occasional rather than constant.",
    "Start with the smallest range that solves the issue, because even dynamic moves can become obvious if pushed too hard.",
    "Choose the band width carefully so you are controlling the problem area without unnecessarily affecting the rest of the sound.",
    "Check attack and release times, because too-fast or too-slow settings can make the result feel unnatural.",
    "Compare against a static EQ move when deciding which approach sounds cleaner and more musical.",
  ];

  const relatedLinks = [
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Start with standard EQ concepts before diving into dynamic frequency-dependent control.",
    },
    {
      title: "Multi-Band Compression",
      to: "/learning/multi-band-compression",
      description:
        "Compare dynamic EQ with multiband workflows and see when each one makes more sense.",
    },
    {
      title: "De-Esser",
      to: "/learning/de-esser",
      description:
        "See one of the most common real-world examples of dynamic EQ-style processing.",
    },
    {
      title: "Sidechain Compression",
      to: "/learning/sidechain-compression",
      description:
        "Learn how external triggers can also be used for more targeted and reactive frequency control.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Dynamic EQ | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Dynamic EQ in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Dynamic EQ is one of the most useful tools for frequency-specific problem solving.
                      It combines ideas from equalization and dynamics processing so that an EQ band only moves when the sound actually needs it.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      That makes it especially good for issues that come and go, such as harshness, sibilance, boominess, or resonances that are not present all the time.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5, lineHeight: 1.2 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    What is dynamic EQ?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Dynamic EQ is an equalizer where one or more bands react dynamically based on signal level.
                    Instead of cutting or boosting all the time, a band can stay still until a target frequency becomes too strong or needs more support.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    For example, a vocal may only become harsh on certain louder notes. A static EQ cut might dull the vocal all the time.
                    A dynamic EQ can reduce that harsh frequency range only when it actually jumps out.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    That is what makes dynamic EQ so useful. It can solve intermittent problems with less permanent tonal damage.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Core dynamic EQ concepts
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {concepts.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Common uses for dynamic EQ
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {commonUses.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Common control points
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Dynamic EQ vs standard EQ and multi-band compression
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Fixed tone shaping vs reactive tone shaping
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Standard EQ is great when a tone issue is constant. Dynamic EQ is better when the issue is only there sometimes.
                        Multi-band compression can solve similar problems, but it often works in broader ranges and can feel less surgical.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Dynamic EQ often sits in a sweet spot between static EQ and multiband compression: more precise than one, often lighter-handed than the other.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Why dynamic EQ works so well
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      Dynamic EQ works well because many mix problems are not constant. Harshness, sibilance, boom, and resonance often appear only on certain notes or phrases.
                      Dynamic EQ responds only when needed, which often preserves more natural tone the rest of the time.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      That is why it is so popular on vocals, mastering chains, and sources that are basically good but occasionally misbehave.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Common mistakes
                      </Typography>
                    </Stack>

                    <Stack spacing={1.35}>
                      {[
                        "Using dynamic EQ for problems that are actually constant and would be better solved by normal EQ.",
                        "Setting too much range and making the band movement obvious or unnatural.",
                        "Choosing a band that is too wide and affecting more tone than necessary.",
                        "Ignoring attack and release timing and ending up with grabby or dull-sounding behavior.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Best practices
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper key={index} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SpeedRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Why dynamic EQ feels more transparent
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.35}>
                      {[
                        "It only changes the sound when the problem actually happens.",
                        "It can leave the tone more intact the rest of the time.",
                        "It is often narrower and more focused than broader dynamic processors.",
                        "It helps reduce overcorrecting with permanent EQ moves.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <SpeedRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Final thoughts
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Dynamic EQ is one of the most useful tools for solving frequency-specific problems without overprocessing the entire signal.
                    It gives you reactive control that often sounds more natural than permanent cuts or boosts.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Used carefully, it can clean up harshness, control boom, and refine difficult sources while preserving more of the tone you actually want to keep.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}